// Agora's native microphone track. This module does not join a channel or publish anything.
// The future transport must publish the returned track itself, observe status, and unpublish on close/error.
import type { IAgoraRTC, IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng';
import type { AIDenoiserExtension, AIDenoiserProcessor, AIDenoiserProcessorLevel } from 'agora-extension-ai-denoiser';

const DENOISER_ASSETS = '/vendor/agora-denoiser/2.0.2';
const DENOISER_TIMEOUT_MS = 10_000;
const DENOISER_RETIRE_WAIT_MS = 750;
let sdkPromise: Promise<IAgoraRTC> | null = null;
let extensionPromise: Promise<AIDenoiserExtension | null> | null = null;
let activeExtension: AIDenoiserExtension | null = null, denoiserRetryAt = 0;
const extensionControllers = new WeakMap<AIDenoiserExtension, AbortController>();
// A timed-out processor can still be initializing. Do not pile more processors on the same stalled loader;
// use the SDK's ordinary input path until its asynchronous destruction has settled.
const retiringProcessors = new Set<Promise<void>>();

/** Load one SDK instance only when an Agora transport is actually used. */
export function loadAgoraSdk(): Promise<IAgoraRTC> {
  if (!sdkPromise) {
    sdkPromise = import('agora-rtc-sdk-ng').then((m) => m.default).catch((e: unknown) => { sdkPromise = null; throw e; });
  }
  return sdkPromise;
}

/** One extension instance per page. A failed import/registration may be retried later. */
async function getExtension(sdk: IAgoraRTC): Promise<AIDenoiserExtension | null> {
  if (!extensionPromise) {
    extensionPromise = import('agora-extension-ai-denoiser').then(({ AIDenoiserExtension }) => {
      const controller = new AbortController();
      const extension = new AIDenoiserExtension({ assetsPath: DENOISER_ASSETS, fetchOptions: { signal: controller.signal } });
      if (!extension.checkCompatibility()) return null;
      sdk.registerExtensions([extension]);
      extensionControllers.set(extension, controller); activeExtension = extension;
      return extension;
    }).catch((e: unknown) => { extensionPromise = null; throw e; });
  }
  return extensionPromise;
}

/** Abort a still-pending shared WASM request; a later retry creates a fresh extension/signal. */
function cancelExtensionLoad(extension: AIDenoiserExtension) {
  const controller = extensionControllers.get(extension);
  if (!controller || controller.signal.aborted) return;
  controller.abort();
  if (activeExtension === extension) { activeExtension = null; extensionPromise = null; }
  // Do not immediately start another copy of the same stalled request when preview hands off to on-board voice.
  denoiserRetryAt = Date.now() + 5000;
}

type DenoiserStatus = 'off' | 'on' | 'unsupported' | 'failed';
type DenoiserSlot = { processor: AIDenoiserProcessor; cancelLoad: () => void; restoreConstraints: () => void;
  onError: (error: Error) => void; onOverload: (elapsedMs?: number) => void };
export type AgoraDenoiseLevelState = { status: 'off' | 'applying' | 'applied' | 'failed'; level: AIDenoiserProcessorLevel | null };
export type AgoraMicUnavailable = 'mic-denied' | 'mic-missing';
/** Only failures from opening the microphone get these user-actionable reasons. */
export class AgoraMicUnavailableError extends Error {
  constructor(readonly reason: AgoraMicUnavailable) { super('Agora microphone unavailable'); }
}
function microphoneUnavailable(error: unknown): AgoraMicUnavailable | null {
  if (!error || typeof error !== 'object') return null;
  const { code, name } = error as { code?: unknown; name?: unknown };
  if (code === 'PERMISSION_DENIED' || name === 'NotAllowedError' || name === 'PermissionDeniedError') return 'mic-denied';
  if (code === 'DEVICE_NOT_FOUND' || name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'mic-missing';
  return null;
}
export type AgoraMicEvent =
  | { type: 'denoiser'; status: DenoiserStatus; error?: unknown }
  | { type: 'denoiser-level'; state: AgoraDenoiseLevelState }
  | { type: 'denoiser-overload'; elapsedMs?: number }
  | { type: 'device-ended' }
  | { type: 'error'; stage: 'mute' | 'device' | 'pipeline'; error: unknown };
export interface AgoraMicOptions {
  microphoneId?: string;
  denoiser?: boolean;
  denoiserLevel?: AIDenoiserProcessorLevel;
  /** Preview must wait for the initially enabled processor to initialize before labelling its meter. */
  waitForDenoiser?: boolean;
  onEvent?: (event: AgoraMicEvent) => void;
}

/**
 * A microphone is created muted and is not exposed until setMuted(true) succeeds. PTT closes the source as soon as
 * release is requested; Agora's setMuted is asynchronous, so callers must not claim instant remote silence without
 * measuring it. `close()` releases the SDK track immediately, including while another operation is awaiting.
 */
export function createAgoraMicrophone(options: AgoraMicOptions = {}) {
  let closed = false, ready = false, muted = true, ptt = false, talking = false, mayOpen = false;
  let signalClosed!: () => void;
  const closedSignal = new Promise<void>((resolve) => { signalClosed = resolve; });
  let switchingDevice = false, sdkMutedConfirmed = false;
  let mic: IMicrophoneAudioTrack | null = null, sdk: IAgoraRTC | null = null;
  let startPromise: Promise<IMicrophoneAudioTrack> | null = null, queue: Promise<void> = Promise.resolve();
  let retirementRetry = false;
  let cooldownTimer: ReturnType<typeof setTimeout> | null = null, cooldownPrefetch = false;
  let wantedDevice = options.microphoneId || '', deviceVersion = 0, wantsDenoiser = !!options.denoiser;
  let wantedLevel: AIDenoiserProcessorLevel = options.denoiserLevel === 'AGGRESSIVE' ? 'AGGRESSIVE' : 'SOFT', levelVersion = 0;
  let slot: DenoiserSlot | null = null;
  const ownRetirements = new Set<Promise<void>>();
  const pendingConstraints = new Set<Promise<unknown>>();
  let denoiserStatus: DenoiserStatus = 'off';
  let levelState: AgoraDenoiseLevelState = { status: 'off', level: null };
  const sourceWaiters = new Set<() => void>();
  const knownSources = new Set<MediaStreamTrack>();

  const emit = (event: AgoraMicEvent) => { try { options.onEvent?.(event); } catch (e) { console.error('agora mic event:', e); } };
  const markLevel = (status: AgoraDenoiseLevelState['status'], level: AIDenoiserProcessorLevel | null = null) => {
    if (closed) return;
    levelState = { status, level }; emit({ type: 'denoiser-level', state: { ...levelState } });
  };
  const markDenoiser = (status: DenoiserStatus, error?: unknown) => {
    if (status === 'off') markLevel('off');
    else if (status === 'failed' || status === 'unsupported') markLevel('failed');
    denoiserStatus = status; emit({ type: 'denoiser', status, ...(error === undefined ? {} : { error }) });
  };
  const ensureOpen = () => { if (closed) throw new Error('Agora microphone is closed'); };
  const wantsAudio = () => !muted && (!ptt || talking);
  const enqueue = <T>(work: () => Promise<T>): Promise<T> => {
    const next = queue.then(work); queue = next.then(() => {}, () => {}); return next;
  };
  const inspectSource = () => {
    try { const source = mic?.getMediaStreamTrack() ?? null; if (source) knownSources.add(source); return { source, failed: false }; }
    catch { return { source: null, failed: true }; }
  };
  const liveSource = () => { const { source } = inspectSource(); return source?.readyState === 'live' ? source : null; };
  const quietKnownSources = () => {
    let failed = false;
    for (const track of knownSources) {
      if (track.readyState !== 'live') { knownSources.delete(track); continue; }
      try { track.enabled = false; if (track.enabled) throw new Error('Could not close microphone source'); }
      catch { failed = true; try { track.stop(); } catch {} }
    }
    return !failed;
  };
  const setSource = (on: boolean) => {
    const track = liveSource();
    // The SDK can briefly report the old, ended track while it swaps in a new processed/device track. A pending
    // track-updated event is gated below; reopening waits until a live source exists.
    if (!track) { if (on) throw new Error('Agora microphone track is unavailable'); return; }
    if (on && !quietKnownSources()) throw new Error('Could not close an old microphone source');
    track.enabled = on;
    if (track.enabled !== on) throw new Error('Could not change Agora microphone source state');
  };
  const quietNow = () => {
    mayOpen = false;
    const { source, failed } = inspectSource(), quiet = quietKnownSources();
    // During a confirmed SDK-muted device swap the SDK may temporarily report the old, ended source. Every known
    // live source is already gated and track-updated gates the replacement before it can be opened.
    const swapping = switchingDevice && sdkMutedConfirmed && !failed;
    if (!quiet || failed || (mic && (!source || source.readyState !== 'live') && !swapping)) {
      emit({ type: 'error', stage: 'mute', error: new Error('Could not confirm microphone source is closed') }); close();
    }
  };
  const onTrackUpdated = (track: MediaStreamTrack) => {
    knownSources.add(track);
    if (closed) track.stop();
    else try {
      if (!quietKnownSources()) throw new Error('Could not close an old microphone source');
      const enabled = mayOpen && wantsAudio(); track.enabled = enabled;
      if (track.enabled !== enabled) throw new Error('Could not gate the replacement microphone track');
    } catch (error) {
      try { track.stop(); } catch {}
      emit({ type: 'error', stage: 'mute', error }); close();
    }
    for (const wake of sourceWaiters) wake();
  };
  const onTrackEnded = () => {
    muted = true; quietNow();
    void enqueue(syncGate).catch(() => {}); emit({ type: 'device-ended' });
  };
  async function waitForLiveSource() {
    if (liveSource()) return;
    await new Promise<void>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout>, poll: ReturnType<typeof setInterval>;
      const finish = (error?: Error) => { clearTimeout(timer); clearInterval(poll); sourceWaiters.delete(check); error ? reject(error) : resolve(); };
      const check = () => { if (closed) finish(new Error('Agora microphone is closed')); else if (liveSource()) finish(); };
      timer = setTimeout(() => finish(new Error('Agora microphone track did not recover')), 5000);
      poll = setInterval(check, 50); sourceWaiters.add(check); check();
    });
  }

  /** Restore the SDK's ordinary track path before destroying a processor. */
  async function detach(current: NonNullable<typeof slot>) {
    if (slot !== current) return;
    slot = null;
    current.cancelLoad();
    current.processor.off('pipeerror', current.onError);
    current.processor.off('overload', current.onOverload);
    try {
      current.processor.unpipe();
      if (mic) { mic.unpipe(); if (!closed) mic.pipe(mic.processorDestination); }
    } catch (error) { emit({ type: 'error', stage: 'pipeline', error }); }
    current.restoreConstraints();
    let retiring: Promise<void>;
    try { retiring = Promise.resolve(current.processor.destroy()); }
    catch (error) { emit({ type: 'error', stage: 'pipeline', error }); return; }
    retiringProcessors.add(retiring); ownRetirements.add(retiring);
    try { await retiring; } catch (error) { emit({ type: 'error', stage: 'pipeline', error }); }
    finally { retiringProcessors.delete(retiring); ownRetirements.delete(retiring); }
  }
  async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([promise, closedSignal.then(() => { throw new Error('Agora microphone is closed'); }),
        new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('Agora denoiser timed out')), ms); })]);
    } finally { clearTimeout(timer); }
  }
  async function applyLevel(current: DenoiserSlot, version: number, target: AIDenoiserProcessorLevel) {
    try {
      await withTimeout(Promise.resolve().then(() => current.processor.setLevel(target)), DENOISER_TIMEOUT_MS);
      if (!closed && wantsDenoiser && slot === current && version === levelVersion) markLevel('applied', target);
    } catch (error) {
      if (!closed && wantsDenoiser && slot === current) current.onError(error instanceof Error ? error : new Error('Agora denoiser level failed'));
    }
  }
  function retryDenoiser() {
    if (closed || !wantsDenoiser || slot || !ready || !mic || Date.now() < denoiserRetryAt) return;
    const track = mic;
    // Gate the existing source before re-piping it, just as an explicit settings change does.
    quietNow(); if (closed) return;
    void enqueue(async () => {
      if (closed || !wantsDenoiser || slot || mic !== track) return;
      try { await setSdkMuted(track, true); if (closed) return; await refreshDenoiser(); await waitForLiveSource(); await syncGate(); }
      catch (error) { quietNow(); emit({ type: 'error', stage: 'pipeline', error }); close(); }
    });
  }
  function retryAfterRetirement() {
    if (retirementRetry || !retiringProcessors.size) return;
    retirementRetry = true;
    void Promise.race([Promise.allSettled([...retiringProcessors]), closedSignal]).then(() => {
      retirementRetry = false; retryDenoiser();
    });
  }
  function retryAfterCooldown() {
    if (cooldownTimer || cooldownPrefetch || closed || !wantsDenoiser) return;
    cooldownTimer = setTimeout(() => {
      cooldownTimer = null;
      if (closed || !wantsDenoiser || !ready || !mic || !sdk || slot) return;
      cooldownPrefetch = true;
      void (async () => {
        let extension: AIDenoiserExtension | null = null, pending = false;
        try {
          extension = await withTimeout(getExtension(sdk!), DENOISER_TIMEOUT_MS);
          if (!extension || closed || !wantsDenoiser || slot) return;
          // Preload without piping or muting the current input. A still-stalled network cannot block its audio.
          const processor = extension.createProcessor();
          const loading = (processor as unknown as { S?: Promise<unknown> }).S;
          if (!loading || typeof loading.catch !== 'function') return;
          pending = true; void loading.then(() => { pending = false; }, () => { pending = false; });
          await withTimeout(loading, DENOISER_TIMEOUT_MS);
          if (retiringProcessors.size) retryAfterRetirement(); else retryDenoiser();
        } catch { if (pending && extension) cancelExtensionLoad(extension); }
        finally { cooldownPrefetch = false; }
      })();
    }, Math.max(0, denoiserRetryAt - Date.now()));
  }
  async function waitForRetirements() {
    if (!retiringProcessors.size) return true;
    try { await withTimeout(Promise.allSettled([...retiringProcessors]), DENOISER_RETIRE_WAIT_MS); } catch {}
    if (!retiringProcessors.size) return true;
    if (!closed && wantsDenoiser) retryAfterRetirement();
    return false;
  }
  async function refreshDenoiser() {
    if (!ready || !mic || !sdk || closed) return;
    if (!wantsDenoiser) {
      if (slot) void detach(slot);
      if (!closed) markDenoiser('off');
      return;
    }
    if (slot) return;
    if (Date.now() < denoiserRetryAt) {
      retryAfterCooldown(); markDenoiser('failed', new Error('Previous Agora denoiser is still closing')); return;
    }
    if (!await waitForRetirements()) {
      if (!closed && wantsDenoiser) markDenoiser('failed', new Error('Previous Agora denoiser is still closing')); return;
    }
    try {
      const extension = await withTimeout(getExtension(sdk), DENOISER_TIMEOUT_MS);
      if (closed || !wantsDenoiser) return;
      if (!extension) { markDenoiser('unsupported'); return; }
      if (Date.now() < denoiserRetryAt) {
        retryAfterCooldown(); markDenoiser('failed', new Error('Previous Agora denoiser is still closing')); return;
      }
      if (!await waitForRetirements()) {
        if (!closed && wantsDenoiser) markDenoiser('failed', new Error('Previous Agora denoiser is still closing')); return;
      }
      if (closed || !wantsDenoiser) return;
      const processor = extension.createProcessor();
      const current = { processor, cancelLoad: () => {}, restoreConstraints: () => {},
        onError: (_error: Error) => {}, onOverload: (elapsedMs?: number) => emit({ type: 'denoiser-overload', elapsedMs }) };
      current.onError = (error: Error) => {
        if (slot !== current) return;
        // A late WASM/pipe failure returns to the SDK's ordinary microphone path and is visible to the caller.
        void detach(current); if (!closed) markDenoiser('failed', error);
      };
      slot = current;
      // Pinned Agora denoiser 2.0.2 starts its WASM fetch in createProcessor(), but attaches its own handler only
      // after an async constraints change. A fast 404 can otherwise become a global unhandled rejection. S is a
      // version-specific internal promise, not an SDK API: keep this guard with the pinned version and re-review
      // it on upgrade. The rejection still runs the normal failure/cleanup path and emits a failure status.
      type ConstraintContext = { requestApplyConstraints: (...args: unknown[]) => Promise<unknown>;
        requestRevertConstraints: (...args: unknown[]) => Promise<unknown> };
      const internal = processor as unknown as { S?: Promise<unknown>; outputNode?: AudioNode;
        onPiped?: (context: ConstraintContext) => void;
        onNode?: (node: AudioNode, context: unknown) => void | Promise<void> };
      const loading = internal.S;
      if (loading && typeof loading.catch === 'function') {
        let pending = true;
        void loading.then(() => { pending = false; }, () => { pending = false; });
        current.cancelLoad = () => { if (pending) cancelExtensionLoad(extension); };
        void loading.catch((error: unknown) => current.onError(error instanceof Error ? error : new Error('Agora denoiser load failed')));
      } else throw new Error('Unsupported Agora denoiser loader');
      // The preview can be cancelled while WASM is loading. Wait before piping so the plugin cannot start a
      // microphone constraint swap that would outlive that cancelled preview.
      if (options.waitForDenoiser) {
        await withTimeout(loading, DENOISER_TIMEOUT_MS);
        if (closed || !wantsDenoiser || slot !== current) { void detach(current); return; }
      }
      // The plugin may open a replacement microphone while applying/reverting constraints. Track those SDK
      // promises so a cancelled preview waits for late device acquisition before the next one can begin.
      const onPiped = internal.onPiped;
      if (typeof onPiped !== 'function') throw new Error('Unsupported Agora denoiser context');
      const contexts = new Set<ConstraintContext>(), restores: Array<() => void> = [];
      current.restoreConstraints = () => {
        for (const restore of restores.splice(0)) {
          try { restore(); } catch (error) { emit({ type: 'error', stage: 'pipeline', error }); }
        }
        contexts.clear();
      };
      internal.onPiped = function (context) {
        try {
          if (contexts.has(context)) return onPiped.call(this, context);
          const apply = context.requestApplyConstraints, revert = context.requestRevertConstraints;
          if (typeof apply !== 'function' || typeof revert !== 'function') throw new Error('Unsupported Agora constraints');
          const observe = (value: Promise<unknown>) => {
            const pending = Promise.resolve(value); pendingConstraints.add(pending);
            void pending.then(() => pendingConstraints.delete(pending), () => pendingConstraints.delete(pending));
            return value;
          };
          const wrappedApply = function (this: ConstraintContext, ...args: unknown[]) {
            const result = apply.apply(this, args); return args[1] === processor ? observe(result) : result;
          };
          const wrappedRevert = function (this: ConstraintContext, ...args: unknown[]) {
            // A closing microphone needs no restored constraints. Reverting here can open a new device after close.
            if (args[0] === processor && closed) return Promise.resolve();
            const result = revert.apply(this, args); return args[0] === processor ? observe(result) : result;
          };
          restores.push(() => {
            if (context.requestApplyConstraints === wrappedApply) context.requestApplyConstraints = apply;
            if (context.requestRevertConstraints === wrappedRevert) context.requestRevertConstraints = revert;
          });
          context.requestApplyConstraints = wrappedApply; context.requestRevertConstraints = wrappedRevert;
          if (context.requestApplyConstraints !== wrappedApply || context.requestRevertConstraints !== wrappedRevert)
            throw new Error('Agora constraints cannot be observed');
          contexts.add(context);
          return onPiped.call(this, context);
        } catch (error) { current.onError(error instanceof Error ? error : new Error('Agora constraints unavailable')); }
      };
      // 2.0.2's onNode awaits initialization before connecting its input. If it settles after unpipe(), it can
      // reconnect that input to a retired worklet. Remove only that late edge, leaving the ordinary SDK path intact.
      // Like S above, onNode/outputNode are protected plugin internals and must be re-reviewed on upgrade.
      const onNode = internal.onNode;
      if (typeof onNode === 'function') internal.onNode = function (node, context) {
        const removeLateEdge = () => {
          if (slot === current) return;
          const output = internal.outputNode;
          if (output && output !== node) {
            try { node.disconnect(output); } catch {}
            try { output.disconnect(); } catch {}
          }
        };
        try { void Promise.resolve(onNode.call(this, node, context)).then(removeLateEdge, (error: unknown) => {
          removeLateEdge(); current.onError(error instanceof Error ? error : new Error('Agora denoiser node failed'));
        }); } catch (error) { removeLateEdge(); current.onError(error instanceof Error ? error : new Error('Agora denoiser node failed')); }
      };
      else throw new Error('Unsupported Agora denoiser node');
      processor.on('pipeerror', current.onError); processor.on('overload', current.onOverload);
      mic.pipe(processor);
      if (closed || !wantsDenoiser || slot !== current) { void detach(current); return; }
      processor.pipe(mic.processorDestination);
      // This SDK's processor starts enabled, so enable() alone can return before its WASM/worklet is ready.
      if (options.waitForDenoiser) {
        await withTimeout(Promise.resolve(processor.disable()), DENOISER_TIMEOUT_MS);
        if (closed || !wantsDenoiser || slot !== current) { void detach(current); return; }
      }
      await withTimeout(Promise.resolve(processor.enable()), DENOISER_TIMEOUT_MS);
      if (closed || !wantsDenoiser || slot !== current) { void detach(current); return; }
      // enable() alone can return while initialization is still pending. Report the selected level only once the
      // pinned SDK confirms it; superseding changes remain marked as applying until their own request succeeds.
      const target = wantedLevel, version = levelVersion; markLevel('applying');
      await withTimeout(Promise.resolve().then(() => processor.setLevel(target)), DENOISER_TIMEOUT_MS);
      if (closed || !wantsDenoiser || slot !== current) { void detach(current); return; }
      if (version === levelVersion) markLevel('applied', target);
      markDenoiser('on');
    } catch (error) {
      // destroy() can itself wait for the same stalled WASM initialization. The graph is unpiped synchronously;
      // finish retiring it in the background so this microphone can use the ordinary SDK path now.
      if (slot) void detach(slot);
      if (!closed && wantsDenoiser) markDenoiser('failed', error);
    }
  }
  async function syncGate() {
    if (!ready || !mic || closed) return;
    const current = mic;
    quietNow();
    if (closed) return;
    try {
      if (!wantsAudio()) { await setSdkMuted(current, true); return; }
      await waitForLiveSource(); if (closed) return;
      if (!wantsAudio()) { await setSdkMuted(current, true); return; }
      await setSdkMuted(current, false); if (closed) return;
      // If mute/release arrived while unmute was awaiting, do not reopen the source and restore SDK mute.
      if (!wantsAudio()) { quietNow(); await setSdkMuted(current, true); return; }
      await waitForLiveSource(); if (closed) return;
      if (!wantsAudio()) { quietNow(); await setSdkMuted(current, true); return; }
      mayOpen = true; setSource(true);
    } catch (error) {
      quietNow(); emit({ type: 'error', stage: 'mute', error }); close(); throw error;
    }
  }
  async function setSdkMuted(track: IMicrophoneAudioTrack, on: boolean) {
    if (!on) sdkMutedConfirmed = false;
    await track.setMuted(on);
    if (on) sdkMutedConfirmed = true;
  }
  async function changeDevice(id: string, version: number) {
    if (!ready || !mic || closed || version !== deviceVersion) return;
    const current = mic;
    try {
      await setSdkMuted(current, true); if (closed) return;
      quietNow(); if (closed) return;
      await waitForLiveSource(); if (closed) return;
      switchingDevice = true;
      try { await current.setDevice(id || 'default'); }
      catch (error) { const reason = microphoneUnavailable(error); if (reason) throw new AgoraMicUnavailableError(reason); throw error; }
      if (closed) return;
      quietNow(); if (closed) return;
      await waitForLiveSource(); if (closed) return;
    } catch (error) {
      quietNow(); emit({ type: 'error', stage: 'device', error });
      // Keep the old device usable if the SDK can still mute it; otherwise syncGate fails closed.
      if (!closed && version === deviceVersion) await syncGate();
      throw error;
    } finally { switchingDevice = false; }
    if (version === deviceVersion) await syncGate();
  }
  function changeGate(update: () => void) {
    ensureOpen();
    const before = wantsAudio(); update(); const after = wantsAudio();
    if (!after) quietNow();
    ensureOpen();
    // A PTT key in open-mic mode, for example, does not need to mute/unmute the published track.
    return before === after ? queue : enqueue(syncGate);
  }
  function close() {
    if (closed) return;
    closed = true; ready = false; mayOpen = false; signalClosed();
    if (cooldownTimer) clearTimeout(cooldownTimer); cooldownTimer = null;
    for (const wake of sourceWaiters) wake();
    const current = mic;
    inspectSource();
    for (const track of knownSources) try { track.stop(); } catch {}
    knownSources.clear();
    if (slot) void detach(slot);
    try { current?.close(); } catch {}
    // Keep track-updated attached until in-flight operations have settled: any late track is stopped immediately.
    void Promise.allSettled([startPromise, queue, ...ownRetirements, ...pendingConstraints]).then(() => {
      try { current?.off('track-updated', onTrackUpdated); current?.off('track-ended', onTrackEnded); } catch {}
    });
  }

  return {
    async released() { while (pendingConstraints.size) await Promise.allSettled([...pendingConstraints]); },
    start(): Promise<IMicrophoneAudioTrack> {
      ensureOpen();
      if (startPromise) return startPromise;
      startPromise = (async () => {
        try {
          sdk = await loadAgoraSdk(); ensureOpen();
          if (!sdk.checkSystemRequirements()) throw new Error('Agora RTC is unsupported in this browser');
          const initialVersion = deviceVersion;
          let track: IMicrophoneAudioTrack;
          try { track = await sdk.createMicrophoneAudioTrack({ AEC: true, AGC: true, ANS: true, ...(wantedDevice && { microphoneId: wantedDevice }) }); }
          catch (error) { const reason = microphoneUnavailable(error); if (reason) throw new AgoraMicUnavailableError(reason); throw error; }
          mic = track;
          if (closed) { try { track.getMediaStreamTrack()?.stop(); } catch {} track.close(); throw new Error('Agora microphone is closed'); }
          track.on('track-updated', onTrackUpdated); track.on('track-ended', onTrackEnded);
          quietNow(); ensureOpen();
          await setSdkMuted(track, true); ensureOpen();
          quietNow(); ensureOpen();
          ready = true;
          await enqueue(async () => {
            if (deviceVersion !== initialVersion) await changeDevice(wantedDevice, deviceVersion);
            await refreshDenoiser(); await waitForLiveSource(); await syncGate();
          });
          ensureOpen(); return track;
        } catch (error) { close(); throw error; }
      })();
      return startPromise;
    },
    track: () => ready && !closed ? mic : null,
    denoiser: () => denoiserStatus,
    denoiserLevel: (): AgoraDenoiseLevelState => closed ? { status: 'off', level: null } : { ...levelState },
    setMuted(value: boolean) { return changeGate(() => { muted = !!value; }); },
    setPushToTalk(value: boolean) { return changeGate(() => { ptt = !!value; }); },
    setTalking(value: boolean) { return changeGate(() => { talking = !!value; }); },
    setDenoiserLevel(value: AIDenoiserProcessorLevel) {
      ensureOpen(); wantedLevel = value === 'AGGRESSIVE' ? 'AGGRESSIVE' : 'SOFT'; const version = ++levelVersion;
      if (!wantsDenoiser) markLevel('off');
      else if (!slot && (denoiserStatus === 'failed' || denoiserStatus === 'unsupported')) markLevel('failed');
      else markLevel('applying');
      return enqueue(async () => {
        const current = slot;
        if (!ready || !current || !wantsDenoiser || closed || version !== levelVersion) return;
        await applyLevel(current, version, wantedLevel);
      });
    },
    setDevice(id: string) { ensureOpen(); wantedDevice = id || ''; const version = ++deviceVersion; quietNow(); ensureOpen(); return enqueue(() => changeDevice(wantedDevice, version)); },
    setDenoiser(value: boolean) {
      ensureOpen(); wantsDenoiser = !!value; quietNow(); ensureOpen();
      levelVersion++;
      if (!wantsDenoiser) markLevel('off'); else markLevel('applying');
      return enqueue(async () => {
        if (!ready || !mic || closed) return;
        // Pipe changes can replace the underlying track. Keep the SDK muted while that happens.
        try {
          await setSdkMuted(mic, true); if (closed) return;
          await refreshDenoiser();
          // Off/on can coalesce before the old processor is detached; reconfirm the final desired level.
          if (!closed && wantsDenoiser && slot && levelState.status === 'applying') await applyLevel(slot, levelVersion, wantedLevel);
          await waitForLiveSource(); await syncGate();
        }
        catch (error) { quietNow(); emit({ type: 'error', stage: 'pipeline', error }); close(); throw error; }
      });
    },
    close,
  };
}
